import type { ChargerOccupancy, ChargerState, CmdError } from '../api/types';
import { t } from '../i18n';
import { useLive } from '../live/store';

const STATES: ChargerOccupancy[] = ['free', 'occupied', 'reserved'];

/**
 * Charger occupancy from GET /api/maps/:id/chargers or ws {type: "chargers"} (shape still settling):
 * `[{pointId|id, state|status, robot|serial|by, robotName}]`, `{chargers: [...]}`, or `{pointId: state | {...}}`.
 * A boolean `occupied` / `reserved` is accepted too. Kept free of api/queries imports (the socket uses it).
 */
export function parseChargers(data: unknown): ChargerState[] {
  const one = (id: unknown, v: unknown): ChargerState | null => {
    const o = (v && typeof v === 'object' ? v : { state: v }) as Record<string, unknown>;
    const pointId = typeof o.pointId === 'string' ? o.pointId : typeof o.id === 'string' ? o.id : typeof id === 'string' ? id : null;
    if (!pointId) return null;
    const raw = o.state ?? o.status;
    const state: ChargerOccupancy = STATES.includes(raw as ChargerOccupancy) ? (raw as ChargerOccupancy) : o.occupied === true ? 'occupied' : o.reserved === true ? 'reserved' : o.occupied === false ? 'free' : 'unknown';
    const robot = [o.robot, o.serial, o.by, o.robotSerial].find((x) => typeof x === 'string') as string | undefined;
    const robotName = typeof o.robotName === 'string' ? o.robotName : null;
    const name = typeof o.name === 'string' ? o.name : null;
    return { pointId, state, robot: robot ?? null, robotName, name };
  };
  const list = Array.isArray(data) ? data : data && typeof data === 'object' ? ((data as Record<string, unknown>).chargers ?? data) : [];
  if (Array.isArray(list)) return list.map((v) => one(undefined, v)).filter((x): x is ChargerState => Boolean(x));
  if (list && typeof list === 'object') return Object.entries(list as Record<string, unknown>).map(([k, v]) => one(k, v)).filter((x): x is ChargerState => Boolean(x));
  return [];
}

/** Charger point name from the occupancy lists already loaded (any map), else the id. */
export function chargerName(pointId: string): string {
  for (const list of Object.values(useLive.getState().chargers)) {
    const c = list.find((x) => x.pointId === pointId);
    if (c?.name) return c.name;
  }
  return pointId;
}

/**
 * Friendly text for `dock` failures (§21.x): DOCK_FAILED {stage: "occupied"} → all chargers busy; other stages →
 * "couldn't dock (stage: …)" with the tried chargers by name. Null for any other error.
 */
export function dockErrorText(err: CmdError | undefined | null): string | null {
  if (!err || err.code !== 'DOCK_FAILED') return null;
  const d = err.data ?? {};
  const stage = typeof d.stage === 'string' ? d.stage : '';
  if (stage === 'occupied') return t('charge.allBusy');
  const tried = Array.isArray(d.tried) ? d.tried.filter((x): x is string => typeof x === 'string').map(chargerName) : [];
  return t('charge.dockFailed', { stage: stage || '?' }) + (tried.length ? ` · ${t('charge.dockTried', { list: tried.join(', ') })}` : '');
}
